import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Archive, Plus, Save, X, ArrowLeft, Eye, Trash2, Search, List, Grid, Users, Package, MapPin, AlertTriangle } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Modal from '../../components/Modal';
import Table from '../../components/Table';
import RowActions from '../../components/RowActions';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/apiService';
import { renderCell, isEmptyCell } from '../../utils/helpers';

const CabinetPlacements = () => {
  // View state: 'list' or 'detail'
  const [view, setView] = useState('list');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Data states
  const [placements, setPlacements] = useState([]);
  const [allPlacements, setAllPlacements] = useState([]);
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    accessoryType: '',
    location: '',
    quantity: 0,
    status: 'à vérifier',
    clientId: '',
  });
  
  const { success: showSuccess, error: showError } = useToast();

  useEffect(() => {
    loadClients();
    loadAllPlacements();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadClientPlacements(selectedClient.id);
    }
  }, [selectedClient]);

  useEffect(() => {
    filterClients();
  }, [searchQuery, clients]);

  const loadClients = async () => {
    try {
      const data = await apiService.getClients();
      setClients(data);
      setFilteredClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllPlacements = async () => {
    try {
      const data = await apiService.getCabinetPlacements(null);
      setAllPlacements(data);
    } catch (error) {
      console.error('Failed to load placements:', error);
    }
  };

  const loadClientPlacements = async (clientId) => {
    try {
      setLoading(true);
      const data = await apiService.getCabinetPlacements(clientId);
      setPlacements(data);
    } catch (error) {
      showError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const filterClients = () => {
    if (!searchQuery) {
      setFilteredClients(clients);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = clients.filter(
      (client) =>
        client.name?.toLowerCase().includes(query) ||
        client.company?.toLowerCase().includes(query)
    );
    setFilteredClients(filtered);
  };

  // Get summary for each client
  const getClientSummary = (clientId) => {
    const clientPlacements = allPlacements.filter(p => p.clientId === clientId);
    const accessoryTypes = new Set(clientPlacements.map(p => p.accessoryType));
    const locations = new Set(clientPlacements.map(p => p.location));
    const totalItems = clientPlacements.reduce((sum, p) => sum + (p.quantity || 0), 0);
    
    return {
      accessoryCount: accessoryTypes.size,
      locationCount: locations.size,
      totalItems,
      hasData: clientPlacements.length > 0
    };
  };

  // Calculate global stats
  const totalClients = clients.length;
  const clientsWithData = clients.filter(c => getClientSummary(c.id).hasData).length;
  const totalAccessories = allPlacements.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const totalLocations = new Set(allPlacements.map(p => p.location)).size;

  const handleViewClient = (client) => {
    setSelectedClient(client);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedClient(null);
    setPlacements([]);
    loadAllPlacements();
  };

  const handleResetClient = async (client) => {
    const confirmed = window.confirm(
      `⚠️ Êtes-vous sûr de vouloir supprimer TOUTES les données d'armoires pour "${client.name}"?\n\nCette action est irréversible.`
    );
    
    if (!confirmed) return;
    
    try {
      const clientPlacements = allPlacements.filter(p => p.clientId === client.id);
      for (const placement of clientPlacements) {
        await apiService.deleteCabinetPlacement(placement.id);
      }
      showSuccess(`Données supprimées pour ${client.name}`);
      loadAllPlacements();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await apiService.updateCabinetPlacement(editingItem.id, formData);
        showSuccess('Emplacement mis à jour');
      } else {
        await apiService.createCabinetPlacement(formData);
        showSuccess('Emplacement ajouté');
      }
      setShowModal(false);
      setEditingItem(null);
      if (selectedClient) loadClientPlacements(selectedClient.id);
      loadAllPlacements();
    } catch (error) {
      showError('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item.id ? item : null);
    setFormData({
      accessoryType: item.accessoryType || '',
      location: item.location || '',
      quantity: item.quantity || 0,
      status: item.status || 'à vérifier',
      clientId: item.clientId || selectedClient?.id || '',
    });
    setShowModal(true);
  };

  const handleViewItem = (item) => {
    handleEdit(item);
  };

  const handleDeleteItem = async (item) => {
    if (!item?.id) return;
    if (!window.confirm(`Supprimer "${item.accessoryType}" à ${item.location} ?`)) return;
    try {
      await apiService.deleteCabinetPlacement(item.id);
      showSuccess('Entrée supprimée');
      if (selectedClient) loadClientPlacements(selectedClient.id);
      loadAllPlacements();
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({ 
      accessoryType: '', 
      location: '', 
      quantity: 0, 
      status: 'à vérifier', 
      clientId: selectedClient?.id || '' 
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'vérifié': return 'bg-green-100 text-green-800';
      case 'à vérifier': return 'bg-yellow-100 text-yellow-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const uniqueAccessories = [...new Set(placements.map(p => p.accessoryType))].sort();
  const uniqueLocations = [...new Set(placements.map(p => p.location))].sort();

  // Table columns for list view
  const columns = [
    {
      header: 'Client',
      accessor: 'name',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-100 rounded-lg">
            <Archive className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="font-semibold text-gray-900">{row.name}</p>
            <p className="text-sm text-gray-500">{row.company}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Types',
      accessor: 'accessoryCount',
      render: (row) => {
        const summary = getClientSummary(row.id);
        return (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${summary.accessoryCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
            {summary.accessoryCount} types
          </span>
        );
      },
    },
    {
      header: 'Emplacements',
      accessor: 'locationCount',
      render: (row) => {
        const summary = getClientSummary(row.id);
        return (
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${summary.locationCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
            {summary.locationCount} lieux
          </span>
        );
      },
    },
    {
      header: 'Total',
      accessor: 'totalItems',
      render: (row) => {
        const summary = getClientSummary(row.id);
        return <span className="text-lg font-bold text-gray-900">{summary.totalItems}</span>;
      },
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (row) => {
        const summary = getClientSummary(row.id);
        return (
          <RowActions
            onView={() => handleViewClient(row)}
            onEdit={() => handleViewClient(row)}
            onDelete={() => handleResetClient(row)}
            disableDelete={!summary.hasData}
          />
        );
      },
    },
  ];

  if (loading && view === 'list' && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
      </div>
    );
  }

  // ============= CLIENT LIST VIEW =============
  if (view === 'list') {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Archive className="w-8 h-8 text-red-600" />
              Emplacement des Armoires
            </h1>
            <p className="text-gray-600 mt-1">Gérez les emplacements des accessoires par client</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'table' ? 'primary' : 'outline'}
              size="sm"
              icon={List}
              onClick={() => setViewMode('table')}
            >
              Tableau
            </Button>
            <Button
              variant={viewMode === 'card' ? 'primary' : 'outline'}
              size="sm"
              icon={Grid}
              onClick={() => setViewMode('card')}
            >
              Cartes
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:shadow-xl transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Clients</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalClients}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:shadow-xl transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg">
                <Archive className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                {totalClients > 0 ? Math.round((clientsWithData / totalClients) * 100) : 0}%
              </span>
            </div>
            <p className="text-sm font-bold text-gray-500 uppercase">Avec données</p>
            <p className="text-3xl font-extrabold text-gray-900">{clientsWithData}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:shadow-xl transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg">
                <Package className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm font-bold text-gray-500 uppercase">Total Accessoires</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalAccessories}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 hover:shadow-xl transition-all"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
            </div>
            <p className="text-sm font-bold text-gray-500 uppercase">Emplacements</p>
            <p className="text-3xl font-extrabold text-gray-900">{totalLocations}</p>
          </motion.div>
        </div>

        {/* Search */}
        <Card>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher un client..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={Search}
              />
            </div>
            <Button variant="outline">Filtres</Button>
          </div>
        </Card>

        {/* Table View */}
        {viewMode === 'table' && (
          <Card>
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{filteredClients.length} client(s)</h2>
            </div>
            <Table columns={columns} data={filteredClients} />
          </Card>
        )}

        {/* Card View */}
        {viewMode === 'card' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients.map((client, index) => {
              const summary = getClientSummary(client.id);
              return (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-3 rounded-lg bg-red-100">
                            <Archive className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{client.name}</h3>
                            <p className="text-sm text-gray-500">{client.company}</p>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-blue-50 rounded-lg p-2">
                          <p className="text-xl font-bold text-blue-600">{summary.accessoryCount}</p>
                          <p className="text-xs text-gray-500">Types</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-xl font-bold text-green-600">{summary.locationCount}</p>
                          <p className="text-xs text-gray-500">Lieux</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-2">
                          <p className="text-xl font-bold text-purple-600">{summary.totalItems}</p>
                          <p className="text-xs text-gray-500">Total</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-end pt-2 border-t border-gray-200">
                        <RowActions
                          onView={() => handleViewClient(client)}
                          onEdit={() => handleViewClient(client)}
                          onDelete={() => handleResetClient(client)}
                          disableDelete={!summary.hasData}
                        />
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {filteredClients.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun client trouvé</h3>
              <p className="text-gray-600">Essayez un autre terme de recherche</p>
            </div>
          </Card>
        )}
      </div>
    );
  }

  // ============= CLIENT DETAIL VIEW =============
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="outline" icon={ArrowLeft} onClick={handleBackToList} className="mb-4">
            Retour aux clients
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Archive className="w-8 h-8 text-red-600" />
            {selectedClient?.name}
          </h1>
          <p className="text-gray-600 mt-1">{selectedClient?.company} - Emplacements des accessoires</p>
        </div>
        <Button variant="primary" icon={Plus} onClick={handleAddNew}>
          Ajouter
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><div className="text-center"><p className="text-3xl font-bold text-blue-600">{uniqueAccessories.length}</p><p className="text-sm text-gray-600">Types d'accessoires</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-green-600">{uniqueLocations.length}</p><p className="text-sm text-gray-600">Emplacements</p></div></Card>
        <Card><div className="text-center"><p className="text-3xl font-bold text-red-600">{placements.reduce((sum, p) => sum + (p.quantity || 0), 0)}</p><p className="text-sm text-gray-600">Total éléments</p></div></Card>
      </div>

      <Card>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
          </div>
        ) : placements.length === 0 ? (
          <div className="text-center py-12">
            <Archive className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucune donnée</h3>
            <Button variant="primary" icon={Plus} onClick={handleAddNew}>Ajouter le premier</Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <p className="text-sm text-gray-500 mb-4 italic">💡 Cliquez sur une cellule pour modifier</p>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th rowSpan="2" className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase border-r border-gray-200 sticky left-0 bg-gray-50">Accessoires</th>
                  {uniqueLocations.map((loc, idx) => (
                    <th key={loc} colSpan="2" className={`px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase ${idx < uniqueLocations.length - 1 ? 'border-r border-gray-200' : ''}`}>{loc}</th>
                  ))}
                </tr>
                <tr className="bg-gray-100">
                  {uniqueLocations.map((loc, idx) => (
                    <React.Fragment key={loc}>
                      <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700">Nombre</th>
                      <th className={`px-2 py-2 text-center text-xs font-semibold text-gray-700 ${idx < uniqueLocations.length - 1 ? 'border-r border-gray-200' : ''}`}>État</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {uniqueAccessories.map((acc) => (
                  <tr key={acc} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 border-r border-gray-200 sticky left-0 bg-white">{acc}</td>
                    {uniqueLocations.map((loc, idx) => {
                      const data = placements.find(p => p.accessoryType === acc && p.location === loc);
                      return (
                        <React.Fragment key={loc}>
                          <td className="px-2 py-3 text-sm text-center cursor-pointer hover:bg-blue-50" onClick={() => handleEdit(data || { accessoryType: acc, location: loc, clientId: selectedClient.id })}>{isEmptyCell(data?.quantity) ? renderCell(data?.quantity) : data.quantity}</td>
                          <td className={`px-2 py-3 text-sm text-center ${idx < uniqueLocations.length - 1 ? 'border-r border-gray-200' : ''}`}>
                            {data?.status ? <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(data.status)}`}>{data.status}</span> : renderCell(null)}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {placements.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Liste détaillée</h2>
          <Table
            columns={[
              { header: 'Accessoire', accessor: 'accessoryType' },
              { header: 'Emplacement', accessor: 'location' },
              {
                header: 'Quantité',
                accessor: 'quantity',
                render: (row) => (isEmptyCell(row.quantity) ? renderCell(row.quantity) : row.quantity),
              },
              {
                header: 'État',
                accessor: 'status',
                render: (row) => (
                  isEmptyCell(row.status) ? renderCell(row.status) : (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
                    {row.status}
                  </span>
                  )
                ),
              },
              {
                header: 'Actions',
                accessor: 'actions',
                render: (row) => (
                  <RowActions
                    onView={() => handleViewItem(row)}
                    onEdit={() => handleEdit(row)}
                    onDelete={() => handleDeleteItem(row)}
                  />
                ),
              },
            ]}
            data={placements}
          />
        </Card>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingItem ? 'Modifier' : 'Ajouter'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Client:</p>
            <p className="font-semibold text-gray-900">{selectedClient?.name} - {selectedClient?.company}</p>
          </div>
          <Input label="Type d'accessoire" value={formData.accessoryType} onChange={(e) => setFormData({ ...formData, accessoryType: e.target.value })} placeholder="Ex: Flexible 70..." required />
          <Input label="Emplacement" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Ex: Bibliothèque DOE..." required />
          <Input label="Quantité" name="quantity" type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 0 })} placeholder="Ex: 2" required />
          <Select label="État" name="status" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} placeholder="Sélectionner l'état" options={[{ value: 'vérifié', label: 'Vérifié' }, { value: 'à vérifier', label: 'À vérifier' }, { value: 'urgent', label: 'Urgent' }]} />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" icon={X} onClick={() => setShowModal(false)}>Annuler</Button>
            <Button type="submit" variant="primary" icon={Save}>{editingItem ? 'Mettre à jour' : 'Ajouter'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CabinetPlacements;
